/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useAppSelector } from 'src/hooks';
import { Grid } from '@mui/material';
import '../../simulation-summary.scss';
import BarChart from './bar-chart';
import './index.scss';
import { BarChartDetails } from '../../types/simulation-summary.model';
import { dummyChartData } from '../../../../../../testData/simulation-summary-data';
import BarChartComponent from './bar-chart-component';
import { ColorData } from '../../utils/constants';
import BarChartWithoutAxis from './bar-chart-without-axis';
import SkeletonForSummary from './skeleton-for-summary';

// dummy chart skeleton component
export const skeletonTemplate = ({
  dummyCharts,
  barWidth,
  axisKeys
}: {
  dummyCharts: any[];
  barWidth: number;
  axisKeys: { key: string; alias: string; color: string }[];
}) => (
  <Grid
    container
    spacing={1}
    alignItems='center'
    style={{ background: '#F9F9F9' }}
  >
    <Grid item md={1}>
      <SkeletonForSummary />
    </Grid>
    <Grid item md={11}>
      <Grid container spacing={1} alignItems='center'>
        {dummyCharts.map((e,i) => (
          <Grid key={i} item xs='auto'>
            <div className='skeleton-div'>
              <div className='skeleton-chart-div' key={`chart_${e}`}>
                <div className='faded-div'>&nbsp;</div>
                <div className='legend-div'>
                  <BarChart
                    barWidthValue={barWidth}
                    barData={dummyChartData}
                    height={60}
                    axisKeys={axisKeys}
                  />
                </div>
              </div>
            </div>
          </Grid>
        ))}
      </Grid>
    </Grid>
  </Grid>
);

export default function BarChartContainer({
  sourceId,
  moduleId,
  scenarioId
}: {
  sourceId: number;
  moduleId: number;
  scenarioId: number;
}) {
  // Getting summary state from store
  const summaryDataState = useAppSelector((state) => state.summary);
  const {
    summaryMetrics: summaryData,
    isFetchingSummaryMetadata: fetchingSummaryData,
    isUnitSelected
  } = summaryDataState;

  const getData = () => {
    if (summaryData.toggle.length) {
      if (!isUnitSelected) {
        return summaryData.metrics.filter(
          (data) => data.toggle === 'UNITS' || data.toggle === ''
        );
      } else {
        return summaryData.metrics.filter(
          (data) => data.toggle === 'TONNES' || data.toggle === ''
        );
      }
    } else {
      return summaryData.metrics;
    }
  };

  const [summaryChartData, setSummaryChartData] = useState(
    summaryData.metrics.length ? getData() : []
  );

  useEffect(() => {
    setSummaryChartData(summaryData.metrics.length ? getData() : []);
  }, [summaryData, isUnitSelected]);


  // Calculating each chart width as per the window size
  const windowWidth = window.innerWidth - window.innerWidth * 0.26;
  const barWidth = windowWidth / 8;
  const keys = summaryData.metrics.map((data: BarChartDetails) => data.metric);
  const dummyCharts = new Array(14 - keys.length).fill(0);
  const axisKeys = [
    {
      key: 'current',
      alias: 'Current',
      color: ColorData.greyColor
    },
    {
      key: 'new',
      alias: 'New',
      color: ColorData.greyColor
    }
  ];

  const labelChart = [];

  summaryData.modules.forEach((element) => {
    labelChart.push({
      key: element.module,
      alias: element.alias,
      color: ColorData.greyColor
    });
  });

  // rendering charts component dynamically as per the summary data
  return (
    <div className='summary-chart-div'>
      {fetchingSummaryData ? (
        skeletonTemplate({ dummyCharts, axisKeys, barWidth })
      ) : (
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={1.5}>
            <div className='chart-legend-div' style={{ overflow: 'hidden' }}>
              <div
                className='chart-title-bold'
                style={{ visibility: 'hidden' }}
              >
                Scenario {scenarioId}
              </div>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                <div className='legend-box' style={{ marginTop: '10px' }}>
                  <div className='current-icon'>&nbsp;</div>
                  {axisKeys[0].alias}
                </div>
                <div className='legend-box' style={{ marginTop: '10px' }}>
                  <div className='new-icon'>&nbsp;</div>
                  {axisKeys[1].alias}
                </div>
                </div>
                <div style={{ minHeight: '188px' }}>
              <div
                className='chart-title-bold'
                style={{ margin: '36px 0px 0px'}}
              >
                Difference
              </div>
              <div style={{ marginTop: '12px' }}>
                <BarChartWithoutAxis
                  barWidthValue={barWidth}
                  barData={{}}
                  zoom={false}
                  height={250}
                  axisKeys={axisKeys}
                  hover
                  hideAxis
                />
              </div>
                </div>
            </div>
          </Grid>
          <Grid item xs={10.5}>
            <div className='master-chart-div-summary'>
              {summaryChartData.map((chartData: BarChartDetails) => (
                <BarChartComponent
                  summaryMetrics={summaryData.modules}
                  chartData={chartData}
                  barWidthValue={barWidth}
                  ColumnCount={summaryChartData.length}
                />
              ))}
            </div>
          </Grid>
        </Grid>
      )}
    </div>
  );
}
